import { Component, h } from "preact";
import { TransactionVerify } from "../models";
import { Subscription } from "rxjs";
import { makeSureTransactionWasVerified } from "../utils";

interface PromotionState {
    transactionVerify?: TransactionVerify;
    expired: boolean;
}

interface PromotionContent {
    image: string;
    imageAlt: string;
    title: string;
    description: string | string[];
    endDate: string;
}

const PROMOTION: PromotionContent = {
    image: "../assets/promotion/Img promo mobile.png",
    imageAlt: "Promotion cashback Celtis Cash",
    title: "Obtenez un cashback de 5 % jusqu'au 31 décembre 2026",
    description: [
        "Choisissez Celtis Cash pour payer via FedaPay.",
        "Recevez 5 % du montant après paiement."
    ] as string[],
    endDate: "2026-12-31"
};

export default class PromotionComponent extends Component<{}, PromotionState> {
    private expirationTimer?: ReturnType<typeof setTimeout>;
    private subscriptions: Subscription[] = [];

    constructor() {
        super();

        this.state = {
            expired: !this.isActive(PROMOTION.endDate)
        };

        this.subscriptions.push(
            makeSureTransactionWasVerified(verifyTrans => {
                this.setState({ transactionVerify: verifyTrans });
            })
        );
    }

    private getExpirationTimestamp(endDate: string): number {
        if (/^\d{4}-\d{2}-\d{2}$/.test(endDate)) {
            const [year, month, day] = endDate.split("-").map(Number);

            // Vérifie que la date initiale est valide
            const date = new Date(year, month - 1, day);

            if (
                date.getFullYear() !== year ||
                date.getMonth() !== month - 1 ||
                date.getDate() !== day
            ) {
                return NaN;
            }

            // La promotion expire à minuit le lendemain
            return new Date(year, month - 1, day + 1).getTime();
        }

        return Date.parse(endDate);
    }

    private isActive(endDate: string): boolean {
        const expiration = this.getExpirationTimestamp(endDate);

        return Number.isFinite(expiration) && expiration > Date.now();
    }

    private scheduleExpiration(endDate: string): void {
        if (this.expirationTimer) {
            clearTimeout(this.expirationTimer);
        }

        const expiration = this.getExpirationTimestamp(endDate);

        const checkExpiration = () => {
            const remainingTime = expiration - Date.now();

            if (!Number.isFinite(expiration) || remainingTime <= 0) {
                this.setState({ expired: true });
                return;
            }

            this.expirationTimer = setTimeout(
                checkExpiration,
                Math.min(remainingTime, 2147483647)
            );
        };

        checkExpiration();
    }

    componentDidMount() {
        this.scheduleExpiration(PROMOTION.endDate);
    }

    componentWillUnmount() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());

        if (this.expirationTimer) {
            clearTimeout(this.expirationTimer);
        }
    }

    render() {
        const { image, imageAlt, title, description } = PROMOTION;

        const visible = !!this.state.transactionVerify && !this.state.expired;

        if (!visible) {
            return null;
        }

        return (
            <div class="promotion-container d-flex align-items-center">
                <img src={image} alt={imageAlt || title} class="img-fluid" />

                <div class="promotion-content flex-grow-1 p-2">
                    <h6 class="promotion-title font-weight-bold">{title}</h6>

                    <div class="promotion-description">
                        {Array.isArray(description) ? (
                            <ul class="mb-0 pl-3">
                                {description.map((item, index) => (
                                    <li key={index}>{item}</li>
                                ))}
                            </ul>
                        ) : (
                            <p class="mb-0">{description}</p>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}
